export type SortableCategory = {
  id: string;
  type: "category";
  label: string;
  collapsed?: boolean;
};

export type SortIndicatorColors = {
  background: string;
  foreground: string;
};

export type SortableList = {
  id: string;
  type: "list";
  label: string;
  taskCount?: number;
  patternCount?: number;
  categoryId: string | null;
  active: boolean;
  protectedItem?: boolean;
  sortIndicatorColors?: SortIndicatorColors;
};

export type SortableListsEntry = SortableCategory | SortableList;

export type CategoryEdgePlacement = "before" | "after";

function quantity(count: number, singular: string) {
  return `${count} ${count === 1 ? singular : `${singular}s`}`;
}

function listTotals(list: SortableList) {
  return [
    quantity(list.taskCount ?? 0, "task"),
    quantity(list.patternCount ?? 0, "pattern"),
  ].join(", ");
}

export function formatListDescription(
  list: SortableList,
  categoryLabel?: string
) {
  const summary = `${list.active ? "" : "OFF: "}${listTotals(list)}`;
  return categoryLabel ? `${categoryLabel} - ${summary}` : summary;
}

function categoryTotals(lists: SortableList[]) {
  return [
    quantity(lists.length, "list"),
    quantity(
      lists.reduce((total, list) => total + (list.taskCount ?? 0), 0),
      "task"
    ),
    quantity(
      lists.reduce((total, list) => total + (list.patternCount ?? 0), 0),
      "pattern"
    ),
  ].join(", ");
}

export function formatCategoryDescription(lists: SortableList[]) {
  const enabled = lists.filter(list => list.active);
  const disabled = lists.filter(list => !list.active);
  const summaries = [];
  if (enabled.length > 0 || disabled.length === 0) {
    summaries.push(categoryTotals(enabled));
  }
  if (disabled.length > 0) {
    summaries.push(`OFF: ${categoryTotals(disabled)}`);
  }
  return summaries.join(" | ");
}

function categoryBlock(layout: SortableListsEntry[], categoryId: string) {
  const start = layout.findIndex(
    entry => entry.id === categoryId && entry.type === "category"
  );
  if (start === -1) return { start: -1, end: -1 };
  let end = start + 1;
  while (
    end < layout.length &&
    layout[end]?.type === "list" &&
    (layout[end] as SortableList).categoryId === categoryId
  ) {
    end += 1;
  }
  return { start, end };
}

function protectedPrefixLength(layout: SortableListsEntry[]) {
  let index = 0;
  while (
    index < layout.length &&
    layout[index]?.type === "list" &&
    (layout[index] as SortableList).protectedItem
  ) {
    index += 1;
  }
  return index;
}

export function moveListOutsideCategory(
  layout: SortableListsEntry[],
  activeId: string,
  categoryId: string,
  placement: CategoryEdgePlacement
): SortableListsEntry[] {
  const active = layout.find(entry => entry.id === activeId);
  if (!active || active.type !== "list" || active.protectedItem) return layout;

  const remaining = layout.filter(entry => entry.id !== activeId);
  const target = categoryBlock(remaining, categoryId);
  if (target.start === -1) return layout;

  const targetIndex = placement === "before" ? target.start : target.end;
  const insertAt = Math.max(protectedPrefixLength(remaining), targetIndex);
  const moved = { ...active, categoryId: null };

  return [...remaining.slice(0, insertAt), moved, ...remaining.slice(insertAt)];
}

export function moveListAfterEntry(
  layout: SortableListsEntry[],
  activeId: string,
  afterId: string
): SortableListsEntry[] {
  const active = layout.find(entry => entry.id === activeId);
  if (!active || active.type !== "list" || active.protectedItem) return layout;

  const remaining = layout.filter(entry => entry.id !== activeId);
  const targetIndex = remaining.findIndex(entry => entry.id === afterId);
  const target = remaining[targetIndex];
  if (!target) return layout;

  const moved = {
    ...active,
    categoryId: target.type === "category" ? target.id : target.categoryId,
  };
  const insertAt = Math.max(protectedPrefixLength(remaining), targetIndex + 1);
  return [...remaining.slice(0, insertAt), moved, ...remaining.slice(insertAt)];
}

export function moveSortableListsEntry(
  layout: SortableListsEntry[],
  activeId: string,
  overId: string
): SortableListsEntry[] {
  if (activeId === overId) return layout;
  const activeIndex = layout.findIndex(entry => entry.id === activeId);
  const overIndex = layout.findIndex(entry => entry.id === overId);
  const active = layout[activeIndex];
  const over = layout[overIndex];
  if (!active || !over || (active.type === "list" && active.protectedItem))
    return layout;
  const movingDown = activeIndex < overIndex;

  if (active.type === "category") {
    const source = categoryBlock(layout, active.id);
    if (source.start === -1) return layout;
    const block = layout.slice(source.start, source.end);
    const remaining = [
      ...layout.slice(0, source.start),
      ...layout.slice(source.end),
    ];
    const overEntry = remaining.find(entry => entry.id === overId);
    if (!overEntry) return layout;
    const targetCategoryId =
      overEntry.type === "category" ? overEntry.id : overEntry.categoryId;
    const targetBlock = targetCategoryId
      ? categoryBlock(remaining, targetCategoryId)
      : null;
    const targetIndex = targetBlock
      ? movingDown
        ? targetBlock.end
        : targetBlock.start
      : remaining.findIndex(entry => entry.id === overId) +
        (movingDown ? 1 : 0);
    const insertAt = Math.max(
      protectedPrefixLength(remaining),
      targetIndex < 0 ? remaining.length : targetIndex
    );
    return [
      ...remaining.slice(0, insertAt),
      ...block,
      ...remaining.slice(insertAt),
    ];
  }

  const remaining = layout.filter(entry => entry.id !== active.id);
  const target = remaining.find(entry => entry.id === overId);
  if (!target) return layout;

  const nextCategoryId =
    target.type === "category" ? target.id : target.categoryId;
  const moved = { ...active, categoryId: nextCategoryId };
  let insertAt = remaining.findIndex(entry => entry.id === target.id);

  if (target.type === "category") {
    insertAt += 1;
  } else if (movingDown) {
    insertAt += 1;
  }

  insertAt = Math.max(protectedPrefixLength(remaining), insertAt);

  return [...remaining.slice(0, insertAt), moved, ...remaining.slice(insertAt)];
}
